import Toolbar from 'app/components/Toolbar';
import React from 'react';
import { RocketOutlined, DownOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Col, PaginationProps, Row, Space } from 'antd';
import Dropdown from 'app/components/Dropdown';
import List from 'app/components/List';
import { Title } from 'app/components/Typography/Title';
import EventCalendar from '../../EventCalendar';
import { JOB_STATUS_OPTION } from 'app/utils/constants';
import moment from 'moment';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import ListEventItem from '../../ListEventItem';
import { useNavigate } from 'react-router';

const StyledList = styled(List)`
  position: relative;
  overflow-x: hidden;
  overflow-y: scroll;
  padding: 0;
`;

const SectionHeader = styled.div`
  position: sticky;
  z-index: 1;
  top: 0;
  background: #f6f9ff;
  height: auto;
  padding-top: 16px;
  padding-bottom: 4px;
`;

const StyledContent = styled.div`
  margin-top: 40px;
`;

type Props = {
  data: any[];
  onChange: (
    filter: { [key: string]: any },
    sort: { [key: string]: any },
    pagination: PaginationProps
  ) => void;
};

export const InspectionCalendar = (props: Partial<Props>) => {
  const { onChange, data } = props;

  const previousRanges = React.useRef('');

  const [filter, setFilter] = React.useState<{
    productTagIds: Array<string | number>; // customerTags is productTags on BE side
    status: Array<string | number>;
    inspectorIds: Array<string | number>;
    areas: Array<string | number>;
  }>({
    productTagIds: ['all'],
    status: ['all'],
    inspectorIds: ['all'],
    areas: ['all']
  });
  const [valueDate, setValueDate] = React.useState(moment(new Date()));
  const [ranges, setRanges] = React.useState<string[]>([]);
  const [, setContainerList] = React.useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  const groupByInspectionDate = React.useMemo(() => {
    if (isEmpty(data)) return [];
    const eventsInDate = data?.filter(
      (item) =>
        moment(item.inspectionDate).format('YYYYMMDD') ===
        valueDate.format('YYYYMMDD')
    );
    return groupBy(
      sortBy(eventsInDate, (item) => {
        return moment(item.inspection.inspectionDate).milliseconds();
      }),
      (item) => {
        return moment(item.inspectionDate).format('DD/MM/YYYY');
      }
    );
  }, [data, valueDate]);

  const onChangeFilter =
    (key: string) => (values: Array<string | number | any> | any) => {
      const nextFilter = {
        ...filter,
        [key]: values
      };

      setFilter(nextFilter);
      const params = {
        // ...resultState.originalArgs,
        current: 1,
        pageSize: 9999
      };
      Object.keys(nextFilter).forEach((key: string) => {
        const item = get(nextFilter, key);
        if (item.length && !item.includes('all')) {
          params[key] = item;
        }
        if (item.includes('all')) {
          delete params[key];
        }
      });
      onChange(nextFilter, {}, { current: 1, pageSize: 9999 });
    };
  React.useLayoutEffect(() => {
    const eventListHTML = document.getElementById('event-list');
    setContainerList(eventListHTML as HTMLElement);
  }, []);

  React.useEffect(() => {
    const firstMonth = valueDate.clone().startOf('month');
    const lastMonth = valueDate.clone().endOf('month');
    setRanges([
      firstMonth.subtract(5, 'd').format('YYYY-MM-DD'),
      lastMonth.add(15, 'd').format('YYYY-MM-DD')
    ]);
  }, [valueDate]);

  React.useEffect(() => {
    if (ranges.length >= 2 && previousRanges.current !== ranges.join(',')) {
      previousRanges.current = ranges.join(',');
      onChange(
        {
          startDate: ranges[0],
          endDate: ranges[1]
        },
        {},
        { current: 1, pageSize: 9999 }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ranges]);

  return (
    <div>
      <Toolbar size={48}>
        <Dropdown.CustomerTags
          onApply={onChangeFilter('productTagIds')}
          onReset={() => {}}
          value={filter.productTagIds}
        ></Dropdown.CustomerTags>

        <Dropdown.FilterDropdown
          key={'status'}
          onApply={onChangeFilter('status')}
          onReset={() => {}}
          value={filter.status}
          options={JOB_STATUS_OPTION.filter(
            ({ value }) => value !== 'pendingAssign'
          )}
          renderLabel={(config) => {
            const { selections } = config;
            return (
              <Button type="text">
                <a
                  type="link"
                  onClick={(e) => {
                    e.preventDefault();
                  }}
                  style={{ color: '#3A4861' }}
                >
                  <Space size={8}>
                    <RocketOutlined />
                    {selections?.includes('all')
                      ? 'All Status'
                      : `Status(${selections?.length ?? ''})`}
                    <DownOutlined />
                  </Space>
                </a>
              </Button>
            );
          }}
          prefix={<RocketOutlined />}
        ></Dropdown.FilterDropdown>
      </Toolbar>
      <StyledContent>
        <Row wrap gutter={16}>
          <Col xl={18} xs={24} md={24} lg={18}>
            <EventCalendar
              renderLabel={({ inspection }) => {
                return (
                  [
                    inspection?.factory?.area?.city,
                    inspection?.factory?.area?.state,
                    inspection?.factory?.area?.country
                  ]
                    .filter((v) => !!v)
                    .join(', ') || 'N/A'
                );
              }}
              onChangeFilterInspectors={onChangeFilter('inspectorIds')}
              onChangeFilterLocations={onChangeFilter('areas')}
              filtered={filter}
              events={data || []}
              value={valueDate.clone()}
              onChange={(newDate) => {
                setValueDate(newDate);
              }}
            ></EventCalendar>
          </Col>
          <Col xl={6} xs={24} md={24} lg={6}>
            <StyledList
              id="event-list"
              height="820px"
              renderItem={(item, index) => {
                const items = get(groupByInspectionDate, item, []);

                return (
                  <div key={index}>
                    <SectionHeader>
                      <Title level={5}>
                        {item} ({items.length})
                      </Title>
                    </SectionHeader>
                    {items.map((event: any, index: number) => {
                      const period = [
                        moment(event.inspection.inspectionDate).format(
                          'DD MMM YY'
                        ),
                        moment(event.inspection.inspectionEndDate).format(
                          'DD MMM YY'
                        )
                      ];
                      return (
                        <ListEventItem
                          onClick={() => {
                            navigate(`job/${event.inspection.id}`);
                          }}
                          key={`${item}_${index}`}
                          period={period}
                          inspection={event.inspection}
                          inspectorName={`${event.inspector?.firstName} ${event.inspector?.lastName}`}
                          status={event?.inspection?.status}
                          phone={event.inspector?.phone}
                        ></ListEventItem>
                      );
                    })}
                  </div>
                );
              }}
              dataSource={Object.keys(groupByInspectionDate)}
            ></StyledList>
          </Col>
        </Row>
      </StyledContent>
    </div>
  );
};
